import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Store, RefreshCw, Loader2, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

const CHANNEL = {
  apple: { label: "App Store", hint: (r) => `commission ${r ?? "—"} % sur le prix hors TVA` },
  google: { label: "Google Play", hint: (r) => `commission ${r ?? "—"} % sur le prix hors TVA` },
  stripe: { label: "Stripe (web)", hint: () => "frais réels Stripe" },
  offline: { label: "Hors-ligne", hint: () => "virement, espèces, PayPal" },
  admin: { label: "Admin", hint: () => "attribué gratuitement" },
};

const fmt = (n, c) => `${(Math.round((n || 0) * 100) / 100).toLocaleString("fr-FR")} ${c === "EUR" ? "€" : c === "USD" ? "$" : c}`;

// Bloc « Par canal d'encaissement » : brut · frais · TVA · net par store, avec le taux administré utilisé.
export const RevenueByChannel = ({ byProvider = {}, rates = {}, rateAlerts = {}, currency = "EUR", isSuper = false, onRecomputed }) => {
  const [busy, setBusy] = useState(false);
  const rows = Object.entries(byProvider).filter(([, v]) => v && (v.count || v.gross)).sort(([a], [b]) => a.localeCompare(b));
  const recompute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/iap/recompute-fees");
      toast.success(data.updated ? `${data.updated} ligne(s) recalculée(s) · net ${fmt(data.net_before, currency)} → ${fmt(data.net_after, currency)}` : "Toutes les lignes store sont déjà exactes");
      onRecomputed?.();
    } catch { toast.error("Recalcul impossible"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border p-3 mt-3" style={{ borderColor: "var(--yalola-border)" }} data-testid="net-rev-channels">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>
          <Store size={13} /> Par canal d'encaissement
        </div>
        {isSuper && (
          <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={recompute} disabled={busy} data-testid="net-rev-recompute-fees" title="Recalcule frais store / TVA / net de toutes les lignes App Store et Google Play (taux RevenueCat réel si connu, sinon taux administré sur le prix hors TVA)">
            {busy ? <Loader2 size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />} Recalculer les frais stores
          </Button>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="text-xs mt-2" style={{ color: "var(--yalola-text-muted)" }} data-testid="net-rev-channels-empty">Aucun encaissement Vérifié / crédits pub pour l'instant.</p>
      ) : (
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-xs" data-testid="net-rev-channels-table">
            <thead>
              <tr className="text-left" style={{ color: "var(--yalola-text-muted)" }}>
                <th className="py-1 pr-3 font-medium">Canal</th>
                <th className="py-1 pr-3 font-medium text-right">Nb</th>
                <th className="py-1 pr-3 font-medium text-right">Brut</th>
                <th className="py-1 pr-3 font-medium text-right">Frais</th>
                <th className="py-1 pr-3 font-medium text-right">TVA</th>
                <th className="py-1 font-medium text-right">Net Yalola</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([p, v]) => {
                const ch = CHANNEL[p] || { label: p, hint: () => "" };
                const al = rateAlerts[p];
                return (
                  <tr key={p} className="border-t" style={{ borderColor: "var(--yalola-border)" }} data-testid={`net-rev-channel-${p}`}>
                    <td className="py-1.5 pr-3 whitespace-nowrap">
                      <b>{ch.label}</b> <span style={{ color: "var(--yalola-text-muted)" }}>· {ch.hint(rates[p])}</span>
                      {v.fx_count > 0 && (
                        <span className="block text-[10px]" style={{ color: "#2D5A4C" }} data-testid={`net-rev-channel-fx-${p}`}>
                          dont {v.fx_count} en {Object.entries(v.fx_native || {}).map(([k, amt]) => `${k} (${fmt(amt, k)})`).join(", ")} → converti{v.fx_count > 1 ? "s" : ""} au taux BCE du jour du paiement
                        </span>
                      )}
                      {al && (
                        <Link to={`/admin?tab=settings&section=finance&focus=set-${p}-fee`} className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: "#FEF3C7", color: "#92400E" }} title={`Écart constaté le ${new Date(al.at).toLocaleDateString("fr-FR")} — clique pour ajuster le % administré`} data-testid={`net-rev-rate-alert-${p}`}>
                          <AlertTriangle size={10} /> taux réel {al.real_rate} % ≠ {al.admin_rate} % réglé
                        </Link>
                      )}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{v.count || 0}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{fmt(v.gross, currency)}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums" style={{ color: "#B91C1C" }}>−{fmt(v.fees, currency)}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums" style={{ color: "#6B7280" }}>−{fmt(v.tax || 0, currency)}</td>
                    <td className="py-1.5 text-right tabular-nums font-bold" style={{ color: "#065F46" }} data-testid={`net-rev-channel-net-${p}`}>{fmt(v.net, currency)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] mt-2" style={{ color: "var(--yalola-text-muted)" }} data-testid="net-rev-channels-note">
        Apple et Google retiennent d'abord la TVA du pays de l'acheteur, puis leur commission sur le prix hors TVA ; RevenueCat affiche le brut converti en dollars. Taux réel RevenueCat utilisé quand il est transmis, sinon taux administré (Paramètres › Crédits publicitaires). Si le taux réel s'écarte d'au moins 1 point du % administré, les super admins sont alertés (1 fois par store et par 24 h).
      </p>
    </div>
  );
};
